"use client";

import React, { useContext, useState } from "react";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import { injectSourceIntoUrl } from "@/config/unified-search-config";
import { Box, Dialog, SxProps } from "@mui/material";
import ViewBrochureOnlineForm from "@/components/forms/view-brochure-online/view-brochure-online-form";
import RequestABrochureSection from "@/components/forms/request-a-brochure/request-a-brochure-section";
import { CtaData, CtaType } from "@/types/dto/common/dynamic-cta";
import useViewBrochureOnline from "@/hooks/forms/use-view-brochure-online";
import GenericHubspotForm from "@/components/forms/generic-hubspot-form/generic-hubspot-form";
import useScrollLock from "@/hooks/scroll-lock/use-scroll-lock";
import DynamicModuleContext from "@/components/landing-page/dynamic-module-context";
import PromoCodeComponent from "../promo-code/promo-code";

interface DynamicCtaButtonProps<T extends CtaType> {
  label: string;
  ctaType: T;
  ctaData: CtaData<T>;
  testId: string;
  variant?: "outlined" | "contained";
  color?: "primary" | "secondary";
  sx?: SxProps;
  targetIdSuffix?: string;
  injectSource?: string;
  startIcon?: React.ReactNode;
  brochureInfo?: {
    brochureName: string;
    brochureQuantity: number;
  };
}

const DynamicCtaButton = <T extends CtaType>({
  label,
  ctaType,
  ctaData,
  testId,
  variant = "contained",
  color = "primary",
  sx = {},
  targetIdSuffix,
  injectSource,
  startIcon,
  brochureInfo
}: DynamicCtaButtonProps<T>) => {
  const isLink = ctaType === "internalLink" || ctaType === "externalLink";
  const moduleId = useContext(DynamicModuleContext);

  // generic hubspot form modal state
  const [hubspotFormIsOpen, setHubspotFormIsOpen] = useState<boolean>(false);

  // request a brochure form modal state
  const [requestBrochureFormIsOpen, setRequestBrochureFormIsOpen] = useState<boolean>(false);

  const hubSpotFormId =
    ctaType === "hubspotFormWithCta"
      ? (ctaData as CtaData<"hubspotFormWithCta">).formId
      : ((ctaData as CtaData<"viewBrochureOnlineForm">).hubSpotForm?.formId ?? "");
  const targetId = `hubspotForm-${hubSpotFormId}-dynamic-cta${targetIdSuffix ? `-${targetIdSuffix}` : ""}${moduleId ? `-${moduleId}` : ""}`;

  // view brochure online hook

  const {
    handleClick: viewBrochureOnlineClick,
    brochureCookieSet,
    viewOnlineDataLayerProps,
    viewOnlineLoading,
    viewOnlineFormIsOpen
  } = useViewBrochureOnline({
    viewBrochureOnlineForm: {
      callToActionText: label,
      callToActionUrl: (ctaData as CtaData<"viewBrochureOnlineForm">).url,
      hubspotFormInfo: (ctaData as CtaData<"viewBrochureOnlineForm">).hubSpotForm
    },
    brochureInfo,
    targetId
  });
  // only the view-brochure CTA becomes a direct link once the brochure cookie is set;
  // other CTA types keep their modal behavior
  const isBrochureLink = ctaType === "viewBrochureOnlineForm" && brochureCookieSet;

  const hrefProp =
    isLink || isBrochureLink
      ? {
          href: injectSourceIntoUrl(
            (ctaData as CtaData<"internalLink"> | CtaData<"externalLink">).url ?? "",
            injectSource ?? `${testId}_cta_button`
          )
        }
      : {};

  const newTabProps =
    isBrochureLink ||
    (ctaType === "externalLink" && (ctaData as CtaData<"externalLink">).openInNewTab)
      ? {
          target: "_blank",
          rel: "noopener noreferrer"
        }
      : {};

  const handleClick = () => {
    if (ctaType === "viewBrochureOnlineForm") {
      viewBrochureOnlineClick();
    }
    if (ctaType === "requestABrochureForm") {
      setRequestBrochureFormIsOpen((prev) => !prev);
    }
    if (ctaType === "hubspotFormWithCta") {
      setHubspotFormIsOpen((prev) => !prev);
    }
  };

  useScrollLock(hubspotFormIsOpen || requestBrochureFormIsOpen || viewOnlineFormIsOpen);

  if (ctaType === "promoCode") {
    return (
      <PromoCodeComponent
        promoCodeLabel={(ctaData as CtaData<"promoCode">).promoCodeLabel}
        promoCode={(ctaData as CtaData<"promoCode">).promoCode}
        testId={testId}
      />
    );
  }

  return (
    <>
      <ColletteButton
        key={testId}
        component={isLink || isBrochureLink ? "a" : "button"}
        variant={variant}
        size="large"
        color={color}
        label={label ?? ""}
        testId={`${testId}-cta-button`}
        startIcon={startIcon}
        data-track="click"
        onClick={isLink ? undefined : handleClick}
        {...hrefProp}
        {...newTabProps}
        sx={variant === "outlined" ? { ...(sx as object), backgroundColor: "common.white" } : sx}
      />
      {/* Pop-ups */}
      {ctaType === "viewBrochureOnlineForm" && (
        <Dialog
          open={viewOnlineFormIsOpen}
          onClose={viewBrochureOnlineClick}
          disableScrollLock
          maxWidth="md"
          data-testid="view-brochure-online-modal"
          keepMounted
        >
          <Box id="landing-page-view-brochure-modal">
            <ViewBrochureOnlineForm
              formId={(ctaData as CtaData<"viewBrochureOnlineForm">)?.hubSpotForm?.formId ?? ""}
              formTitle={
                (ctaData as CtaData<"viewBrochureOnlineForm">)?.hubSpotForm?.formTitle ?? ""
              }
              dataLayerProps={viewOnlineDataLayerProps}
              isLoading={viewOnlineLoading}
              handleClose={viewBrochureOnlineClick}
              targetId={targetId}
            />
          </Box>
        </Dialog>
      )}
      {ctaType === "requestABrochureForm" && (
        <Dialog
          open={requestBrochureFormIsOpen}
          onClose={handleClick}
          disableScrollLock
          maxWidth="md"
          data-testid="request-a-brochure-modal"
          keepMounted
          fullWidth
        >
          <RequestABrochureSection
            handleClose={handleClick}
            brochureDisplay={(ctaData as CtaData<"requestABrochureForm">).brochureDisplay}
            isLandingPage
            preSelectedBrochure={
              (ctaData as CtaData<"requestABrochureForm">)?.brochureDisplay?.preselectedBrochure
            }
          />
        </Dialog>
      )}
      {ctaType === "hubspotFormWithCta" && (
        <Dialog open={hubspotFormIsOpen} onClose={handleClick} keepMounted>
          <GenericHubspotForm
            form={ctaData as CtaData<"hubspotFormWithCta">}
            handleClose={handleClick}
            testId={(ctaData as CtaData<"hubspotFormWithCta">).formName}
            targetId={targetId}
          />
        </Dialog>
      )}
    </>
  );
};

export default DynamicCtaButton;
